import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, Save, ArrowUp, ArrowDown, X, Users, TrendingUp, BarChart3, Mail, Phone,
} from "lucide-react";
import { API } from "@/lib/auth";

/**
 * Quiz Editor — three tabs:
 *   1. Questions & answers (with tag chips per answer)
 *   2. Methods (recommendation cards) & their scoring tags
 *   3. Submissions log + analytics
 * Admin can edit everything without touching code.
 */
export default function QuizEditor() {
  const [tab, setTab] = useState("questions");
  const tabs = [
    { k: "questions", l: "Questions", Icon: Users },
    { k: "methods", l: "Methods", Icon: BarChart3 },
    { k: "submissions", l: "Submissions", Icon: TrendingUp },
  ];
  return (
    <div className="space-y-6" data-testid="admin-quiz-panel">
      <div className="flex gap-1.5 flex-wrap">
        {tabs.map(({ k, l, Icon }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`quiz-tab-${k}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-colors ${
              tab === k
                ? "bg-[hsl(var(--burgundy))] text-[hsl(var(--cream))]"
                : "bg-white border border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.7)] hover:border-[hsl(var(--burgundy))]"
            }`}
          >
            <Icon size={12} /> {l}
          </button>
        ))}
      </div>

      {tab === "questions" && <QuestionsEditor />}
      {tab === "methods" && <MethodsEditor />}
      {tab === "submissions" && <SubmissionsView />}
    </div>
  );
}


// ------------------------------------------------------------
// Questions editor
// ------------------------------------------------------------
function QuestionsEditor() {
  const [questions, setQuestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/quiz/config`);
      setQuestions((r.data?.questions || []).map((q, i) => ({ ...q, order: q.order ?? i + 1 })));
      setDirty(false);
    } catch {
      toast.error("Could not load quiz questions");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = questions.map((q, i) => ({
        id: q.id, order: i + 1, prompt: q.prompt, active: q.active !== false,
        answers: (q.answers || []).map((a) => ({
          label: a.label || "",
          tags: Array.isArray(a.tags) ? a.tags : (a.tags || "").split(",").map((s) => s.trim()).filter(Boolean),
        })),
      }));
      await axios.put(`${API}/admin/quiz/questions`, payload);
      toast.success("Questions saved");
      setDirty(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const mutate = (idx, patch) => {
    setQuestions((qs) => qs.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
    setDirty(true);
  };
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= questions.length) return;
    const next = [...questions];
    [next[idx], next[j]] = [next[j], next[idx]];
    setQuestions(next); setDirty(true);
  };
  const remove = (idx) => {
    if (!window.confirm("Delete this question?")) return;
    setQuestions((qs) => qs.filter((_, i) => i !== idx)); setDirty(true);
  };
  const addQuestion = () => {
    setQuestions((qs) => [...qs, {
      id: null, order: qs.length + 1, prompt: "New question", active: true,
      answers: [{ label: "Option 1", tags: [] }],
    }]);
    setDirty(true);
  };
  const addAnswer = (qIdx) => {
    mutate(qIdx, { answers: [...(questions[qIdx].answers || []), { label: "New option", tags: [] }] });
  };
  const removeAnswer = (qIdx, aIdx) => {
    mutate(qIdx, { answers: (questions[qIdx].answers || []).filter((_, i) => i !== aIdx) });
  };
  const editAnswer = (qIdx, aIdx, patch) => {
    const next = [...(questions[qIdx].answers || [])];
    next[aIdx] = { ...next[aIdx], ...patch };
    mutate(qIdx, { answers: next });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="overline">Quiz questions</div>
          <p className="text-sm text-[hsl(var(--onyx)/0.6)] mt-1">
            Reorder, edit or add answers. Tag each answer with keywords that match your method scoring rules
            (e.g. <em>fine</em>, <em>ponytails</em>, <em>hair_loss</em>).
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={addQuestion} data-testid="quiz-question-add" className="btn-pill btn-outline !text-xs">
            <Plus size={14} /> Add question
          </button>
          <button
            onClick={save}
            disabled={busy || !dirty}
            data-testid="quiz-questions-save"
            className="btn-pill btn-primary !text-xs disabled:opacity-50"
          >
            <Save size={14} /> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="space-y-5">
        {questions.map((q, qIdx) => (
          <div key={q.id || qIdx} className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="overline text-[hsl(var(--gold))]">Question {qIdx + 1}</div>
                <input
                  value={q.prompt}
                  onChange={(e) => mutate(qIdx, { prompt: e.target.value })}
                  data-testid={`quiz-question-${qIdx}-prompt`}
                  className="mt-2 w-full font-display text-2xl bg-transparent border-b border-transparent focus:border-[hsl(var(--burgundy))] outline-none"
                />
              </div>
              <div className="flex gap-1">
                <IconBtn onClick={() => move(qIdx, -1)} title="Move up"><ArrowUp size={14} /></IconBtn>
                <IconBtn onClick={() => move(qIdx, 1)} title="Move down"><ArrowDown size={14} /></IconBtn>
                <IconBtn onClick={() => remove(qIdx)} title="Delete" danger><Trash2 size={14} /></IconBtn>
              </div>
            </div>

            <div className="mt-5 space-y-3">
              {(q.answers || []).map((a, aIdx) => (
                <div key={aIdx} className="rounded-2xl bg-blush/30 border border-[hsl(var(--onyx)/0.05)] p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 space-y-2">
                      <input
                        value={a.label}
                        onChange={(e) => editAnswer(qIdx, aIdx, { label: e.target.value })}
                        data-testid={`quiz-answer-${qIdx}-${aIdx}-label`}
                        placeholder="Answer label"
                        className="w-full h-10 px-4 rounded-full bg-white border border-black/10 text-sm outline-none focus:border-[hsl(var(--burgundy))]"
                      />
                      <input
                        value={Array.isArray(a.tags) ? a.tags.join(", ") : (a.tags || "")}
                        onChange={(e) => editAnswer(qIdx, aIdx, {
                          tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                        })}
                        data-testid={`quiz-answer-${qIdx}-${aIdx}-tags`}
                        placeholder="tags, comma separated (e.g. fine, invisible, ponytails)"
                        className="w-full h-9 px-4 rounded-full bg-white border border-black/10 text-xs text-[hsl(var(--onyx)/0.75)] outline-none focus:border-[hsl(var(--burgundy))]"
                      />
                    </div>
                    <button
                      onClick={() => removeAnswer(qIdx, aIdx)}
                      data-testid={`quiz-answer-${qIdx}-${aIdx}-delete`}
                      className="mt-1 w-8 h-8 rounded-full hover:bg-red-50 text-[hsl(var(--onyx)/0.5)] hover:text-red-500 flex items-center justify-center"
                    ><X size={14} /></button>
                  </div>
                </div>
              ))}
              <button
                onClick={() => addAnswer(qIdx)}
                data-testid={`quiz-question-${qIdx}-add-answer`}
                className="text-xs text-[hsl(var(--burgundy))] hover:underline"
              >
                + Add answer
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ------------------------------------------------------------
// Methods editor
// ------------------------------------------------------------
function MethodsEditor() {
  const [methods, setMethods] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/quiz/config`);
      setMethods((r.data?.methods || []).map((m, i) => ({ ...m, order: m.order ?? i + 1 })));
      setDirty(false);
    } catch {
      toast.error("Could not load methods");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = methods.map((m, i) => ({
        id: m.id, order: i + 1,
        slug: m.slug || m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
        name: m.name || "",
        tag: m.tag || "",
        why: m.why || "",
        benefits: Array.isArray(m.benefits) ? m.benefits : (m.benefits || "").split("\n").map((s) => s.trim()).filter(Boolean),
        maintenance: m.maintenance || "",
        appointment_time: m.appointment_time || "",
        image: m.image || "",
        pricing_url: m.pricing_url || "/shop?category=Hair%20Extensions",
        aftercare_url: m.aftercare_url || "/hair-loss",
        tags: Array.isArray(m.tags) ? m.tags : (m.tags || "").split(",").map((s) => s.trim()).filter(Boolean),
        active: m.active !== false,
      }));
      await axios.put(`${API}/admin/quiz/methods`, payload);
      toast.success("Methods saved");
      setDirty(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const mutate = (idx, patch) => {
    setMethods((ms) => ms.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
    setDirty(true);
  };
  const remove = (idx) => {
    if (!window.confirm("Delete this method?")) return;
    setMethods((ms) => ms.filter((_, i) => i !== idx)); setDirty(true);
  };
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= methods.length) return;
    const next = [...methods];
    [next[idx], next[j]] = [next[j], next[idx]];
    setMethods(next); setDirty(true);
  };
  const add = () => {
    setMethods((ms) => [...ms, {
      id: null, order: ms.length + 1, slug: "", name: "New method", tag: "",
      why: "", benefits: [], maintenance: "", appointment_time: "", image: "",
      pricing_url: "/shop?category=Hair%20Extensions", aftercare_url: "/hair-loss",
      tags: [], active: true,
    }]);
    setDirty(true);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="overline">Recommendation methods</div>
          <p className="text-sm text-[hsl(var(--onyx)/0.6)] mt-1">
            Each method has scoring tags — the more tags overlap with a client's answers, the higher it ranks.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={add} data-testid="quiz-method-add" className="btn-pill btn-outline !text-xs">
            <Plus size={14} /> Add method
          </button>
          <button
            onClick={save}
            disabled={busy || !dirty}
            data-testid="quiz-methods-save"
            className="btn-pill btn-primary !text-xs disabled:opacity-50"
          >
            <Save size={14} /> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {methods.map((m, idx) => (
          <div key={m.id || idx} className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <input
                  value={m.name}
                  onChange={(e) => mutate(idx, { name: e.target.value })}
                  data-testid={`quiz-method-${idx}-name`}
                  className="w-full font-display text-2xl bg-transparent border-b border-transparent focus:border-[hsl(var(--burgundy))] outline-none"
                />
                <input
                  value={m.slug || ""}
                  onChange={(e) => mutate(idx, { slug: e.target.value })}
                  placeholder="slug (auto if empty)"
                  className="mt-2 w-full h-8 px-3 rounded-full bg-white border border-black/10 text-xs outline-none focus:border-[hsl(var(--burgundy))]"
                />
              </div>
              <div className="flex gap-1">
                <IconBtn onClick={() => move(idx, -1)} title="Move up"><ArrowUp size={14} /></IconBtn>
                <IconBtn onClick={() => move(idx, 1)} title="Move down"><ArrowDown size={14} /></IconBtn>
                <IconBtn onClick={() => remove(idx)} title="Delete" danger><Trash2 size={14} /></IconBtn>
              </div>
            </div>

            <Field label="Short tagline" value={m.tag} onChange={(v) => mutate(idx, { tag: v })} />
            <Field label="Why this suits you (long)" value={m.why} onChange={(v) => mutate(idx, { why: v })} multiline />
            <Field
              label="Benefits (one per line)"
              value={Array.isArray(m.benefits) ? m.benefits.join("\n") : m.benefits}
              onChange={(v) => mutate(idx, { benefits: v.split("\n").map((s) => s.trim()).filter(Boolean) })}
              multiline
            />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="Maintenance" value={m.maintenance} onChange={(v) => mutate(idx, { maintenance: v })} inline />
              <Field label="Appointment time" value={m.appointment_time} onChange={(v) => mutate(idx, { appointment_time: v })} inline />
            </div>
            <Field label="Image URL" value={m.image} onChange={(v) => mutate(idx, { image: v })} />
            {m.image && (
              <div className="mt-2 aspect-[16/9] rounded-2xl overflow-hidden bg-blush">
                <img src={m.image} alt={m.name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="Pricing URL" value={m.pricing_url} onChange={(v) => mutate(idx, { pricing_url: v })} inline />
              <Field label="Aftercare URL" value={m.aftercare_url} onChange={(v) => mutate(idx, { aftercare_url: v })} inline />
            </div>
            <Field
              label="Scoring tags (comma separated)"
              value={Array.isArray(m.tags) ? m.tags.join(", ") : m.tags}
              onChange={(v) => mutate(idx, { tags: v.split(",").map((s) => s.trim()).filter(Boolean) })}
            />
            <label className="mt-3 flex items-center gap-2 text-xs text-[hsl(var(--onyx)/0.7)]">
              <input
                type="checkbox"
                checked={m.active !== false}
                onChange={(e) => mutate(idx, { active: e.target.checked })}
              />
              Show in quiz results
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}


// ------------------------------------------------------------
// Submissions view
// ------------------------------------------------------------
function SubmissionsView() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/quiz/submissions`);
      setData(r.data);
    } catch {
      toast.error("Could not load submissions");
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await axios.delete(`${API}/admin/quiz/submissions/${id}`);
      toast.success("Deleted");
      if (selected?.id === id) setSelected(null);
      await load();
    } catch { toast.error("Delete failed"); }
  };

  if (!data) return <div className="text-sm text-[hsl(var(--onyx)/0.5)]">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="quiz-submissions-view">
      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Starts" value={data.total_starts} />
        <Stat label="Completions" value={data.total_complete} />
        <Stat label="Completion rate" value={`${Math.round((data.completion_rate || 0) * 100)}%`} />
        <Stat label="Total leads" value={data.submissions?.length || 0} />
      </div>

      {data.method_counts?.length > 0 && (
        <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
          <div className="overline">Recommended methods breakdown</div>
          <div className="mt-4 space-y-2">
            {data.method_counts.map((m) => (
              <div key={m.slug} className="flex items-center gap-3 text-sm">
                <div className="w-40 truncate text-[hsl(var(--onyx)/0.75)]">{m.slug}</div>
                <div className="flex-1 h-2 rounded-full bg-[hsl(var(--onyx)/0.06)] overflow-hidden">
                  <div
                    className="h-full bg-[hsl(var(--burgundy))]"
                    style={{ width: `${Math.min(100, (m.count / (data.method_counts[0]?.count || 1)) * 100)}%` }}
                  />
                </div>
                <div className="w-8 text-right text-[hsl(var(--onyx)/0.6)]">{m.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-12">
        <div className="lg:col-span-5 rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-4 max-h-[600px] overflow-y-auto">
          <div className="overline mb-3 px-2">Recent leads</div>
          {(data.submissions || []).length === 0 && (
            <div className="text-sm text-[hsl(var(--onyx)/0.5)] px-2">No completed submissions yet.</div>
          )}
          <div className="space-y-1">
            {(data.submissions || []).map((s) => (
              <button
                key={s.id}
                onClick={() => setSelected(s)}
                data-testid={`quiz-submission-${s.id}`}
                className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-colors ${
                  selected?.id === s.id ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]" : "hover:bg-blush/40"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium truncate">{s.name}</span>
                  <span className="text-[10px] uppercase tracking-widest opacity-60 shrink-0">
                    {new Date(s.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="text-xs opacity-70 truncate">{s.email}</div>
                <div className="text-xs mt-1 opacity-80 flex items-center gap-2 flex-wrap">
                  <span>→ {s.top?.name}</span>
                  {s.completion_email_sent_at ? (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[hsl(var(--gold)/0.18)] text-[hsl(var(--gold))]"
                      title="Immediate completion email delivered"
                    >
                      email sent
                    </span>
                  ) : null}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7 rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
          {!selected && <div className="text-sm text-[hsl(var(--onyx)/0.5)]">Select a submission to view details.</div>}
          {selected && (
            <div className="space-y-4">
              <div>
                <h4 className="font-display text-2xl">{selected.name}</h4>
                <div className="text-xs text-[hsl(var(--onyx)/0.55)] mt-1">
                  {new Date(selected.created_at).toLocaleString()}
                </div>
              </div>
              <div className="grid gap-2 text-sm">
                <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-2 text-[hsl(var(--burgundy))] hover:underline">
                  <Mail size={14} /> {selected.email}
                </a>
                {selected.phone && (
                  <a href={`tel:${selected.phone}`} className="inline-flex items-center gap-2 text-[hsl(var(--burgundy))] hover:underline">
                    <Phone size={14} /> {selected.phone}
                  </a>
                )}
                <div className="text-xs text-[hsl(var(--onyx)/0.55)]">
                  Marketing opt-in: <strong>{selected.opt_in ? "Yes" : "No"}</strong>
                </div>
                <div className="text-xs text-[hsl(var(--onyx)/0.55)]">
                  Completion email:{" "}
                  {selected.completion_email_sent_at ? (
                    <strong className="text-[hsl(var(--gold))]">
                      Sent {new Date(selected.completion_email_sent_at).toLocaleString(undefined, {
                        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
                      })}
                    </strong>
                  ) : (
                    <strong className="text-[hsl(var(--burgundy))]">Not sent</strong>
                  )}
                </div>
                {selected.follow_up_sent_at && (
                  <div className="text-xs text-[hsl(var(--onyx)/0.55)]">
                    24h follow-up:{" "}
                    <strong className="text-[hsl(var(--gold))]">
                      Sent {new Date(selected.follow_up_sent_at).toLocaleString(undefined, {
                        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
                      })}
                    </strong>
                  </div>
                )}
              </div>
              <div className="rounded-2xl bg-blush/40 p-4">
                <div className="overline text-[hsl(var(--gold))]">Top match</div>
                <div className="font-display text-xl mt-1">{selected.top?.name}</div>
                <div className="text-xs text-[hsl(var(--onyx)/0.55)]">Score {selected.top?.score}</div>
                {selected.second && (
                  <div className="mt-3 pt-3 border-t border-black/5">
                    <div className="overline text-[hsl(var(--gold))]">Also recommended</div>
                    <div className="font-display text-lg mt-1">{selected.second?.name}</div>
                    <div className="text-xs text-[hsl(var(--onyx)/0.55)]">Score {selected.second?.score}</div>
                  </div>
                )}
              </div>
              <div>
                <div className="overline">Answers</div>
                <div className="mt-2 space-y-2">
                  {(selected.answers || []).map((a, i) => (
                    <div key={i} className="text-sm border-b border-black/5 pb-2">
                      <div className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">
                        Q{a.order} · {a.prompt}
                      </div>
                      <div className="mt-1">{a.answer}</div>
                    </div>
                  ))}
                </div>
              </div>
              <button
                onClick={() => remove(selected.id)}
                className="text-xs text-red-500 hover:underline inline-flex items-center gap-1"
              >
                <Trash2 size={12} /> Delete submission
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ------------------------------------------------------------
// Small helpers
// ------------------------------------------------------------
function IconBtn({ onClick, title, danger, children }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
        danger ? "hover:bg-red-50 text-[hsl(var(--onyx)/0.5)] hover:text-red-500"
               : "hover:bg-blush/60 text-[hsl(var(--onyx)/0.6)]"
      }`}
    >{children}</button>
  );
}

function Field({ label, value, onChange, multiline, inline }) {
  return (
    <label className={`block ${inline ? "" : "mt-3"}`}>
      <span className="overline">{label}</span>
      {multiline ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          className="mt-1.5 w-full rounded-2xl bg-white border border-black/10 px-4 py-3 text-sm outline-none focus:border-[hsl(var(--burgundy))]"
        />
      ) : (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1.5 w-full h-10 rounded-full bg-white border border-black/10 px-4 text-sm outline-none focus:border-[hsl(var(--burgundy))]"
        />
      )}
    </label>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-[hsl(var(--onyx)/0.06)] p-5">
      <div className="overline">{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
    </div>
  );
}
